/**
 * Bank Service
 * 
 * Service for fetching and managing bank codes and branches from the backend.
 * Integrates with the backend's defaults API endpoint for bank resolution.
 * 
 * Evidence: Backend provides /api/v1/group/defaults/bank-codes-and-branches endpoint
 * API Documentation: Returns bank data with structure: {"GCB": {"bank_code": "GCB", "bank_name": "GCB BANK LTD", "branches": [...]}}
 * Purpose: Resolves SLAMS bank_code to readable bank_name for user display
 */

import { apiClient } from '../utils/apiClient';

// Cache for bank data to avoid repeated API calls
let bankDataCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds

/**
 * Convert flat array of bank/branch records to hierarchical structure
 * @param {Array} flatArray - Array of bank/branch records from backend
 * @returns {Object} Hierarchical bank data object
 */
const convertFlatArrayToHierarchical = (flatArray) => {
  const hierarchical = {};
  
  flatArray.forEach(record => {
    const { bankCode, bankName, branchId, branchName } = record;
    
    // Initialize bank if not exists
    if (!hierarchical[bankCode]) {
      hierarchical[bankCode] = {
        bank_code: bankCode,
        bank_name: bankName,
        branches: []
      };
    }
    
    // Add branch to bank
    hierarchical[bankCode].branches.push({
      branch_id: branchId,
      branch_name: branchName,
      bank_code: bankCode
    });
  });
  
  console.log('🔄 BankService: Converted flat array to hierarchical:', {
    originalCount: flatArray.length,
    bankCount: Object.keys(hierarchical).length,
    sampleBank: Object.keys(hierarchical)[0]
  });
  
  return hierarchical;
};

/**
 * Fetch bank codes and branches data from the backend
 * @returns {Promise<Object>} Bank data object with bank codes as keys
 * @throws {Error} If API call fails
 */
const fetchBankCodesAndBranches = async () => {
  try {
    console.log('🏦 BankService: Starting fetchBankCodesAndBranches...');
    console.log('🌐 BankService: Calling API endpoint: defaults/bank-codes-and-branches');
    
    const response = await apiClient.get('defaults/bank-codes-and-branches');
    
    console.log('📡 BankService: Raw API response received:', {
      status: response.status,
      headers: response.headers,
      hasData: !!response.data,
      dataType: typeof response.data,
      dataConstructor: response.data?.constructor?.name,
      dataKeys: response.data && typeof response.data === 'object' ? Object.keys(response.data) : 'N/A'
    });
    
    console.log('📋 BankService: Full response.data structure:', response.data);
    
    let bankData = null;
    
    // Check if response.data.data is an array of bank/branch records (new format)
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      console.log('✅ BankService: Found flat array format, converting to hierarchical');
      bankData = convertFlatArrayToHierarchical(response.data.data);
    }
    // Check if response.data is the object we need (legacy format)
    else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
      // Look for bank codes as top-level keys
      const keys = Object.keys(response.data);
      const firstKey = keys[0];
      const firstValue = response.data[firstKey];
      
      // Check if first value has bank_code and bank_name properties
      if (firstValue && typeof firstValue === 'object' && firstValue.bank_code && firstValue.bank_name) {
        console.log('✅ BankService: response.data is bank data object, keys:', keys.slice(0, 5));
        bankData = response.data;
      }
    }
    
    // Check if it's wrapped in a data property (legacy format)
    if (!bankData && response.data && response.data.data && typeof response.data.data === 'object' && !Array.isArray(response.data.data)) {
      const keys = Object.keys(response.data.data);
      const firstKey = keys[0];
      const firstValue = response.data.data[firstKey];
      
      if (firstValue && typeof firstValue === 'object' && firstValue.bank_code && firstValue.bank_name) {
        console.log('✅ BankService: Found bank data in response.data.data, keys:', keys.slice(0, 5));
        bankData = response.data.data;
      }
    }
    
    if (!bankData) {
      console.error('❌ BankService: Unexpected response structure - no bank data found');
      console.error('🔍 BankService: Response structure debug:', {
        hasData: !!response.data,
        dataType: typeof response.data,
        dataKeys: response.data && typeof response.data === 'object' ? Object.keys(response.data) : 'N/A',
        fullData: response.data
      });
      
      // Return empty object as fallback
      console.warn('⚠️ BankService: Returning empty object as fallback');
      return {};
    }
    
    // Cache the bank data
    bankDataCache = bankData;
    cacheTimestamp = Date.now();
    
    console.log('✅ BankService: Bank data cached successfully, bank count:', Object.keys(bankData).length);
    console.log('🎯 BankService: Sample bank data structure:', Object.entries(bankData)[0]);
    
    return bankData;
    
  } catch (error) {
    console.error('❌ BankService: Error in fetchBankCodesAndBranches:', {
      message: error.message,
      status: error.status,
      response: error.response,
      stack: error.stack
    });
    
    // Return empty object on error
    console.warn('⚠️ BankService: Returning empty object due to error');
    return {};
  }
};

/**
 * Get cached bank data or fetch if cache is expired
 * @returns {Promise<Object>} Bank data object
 */
const getBankData = async () => {
  const now = Date.now();
  
  // Check if cache is valid
  if (bankDataCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
    console.log('💾 BankService: Using cached bank data');
    return bankDataCache;
  }
  
  console.log('🔄 BankService: Cache expired or empty, fetching fresh bank data');
  return await fetchBankCodesAndBranches();
};

/**
 * Resolve bank code to bank name
 * @param {string} bankCode - Bank code from SLAMS (e.g., "GCB", "ABH")
 * @returns {Promise<string>} Readable bank name or the original code if not found
 */
const resolveBankName = async (bankCode) => {
  try {
    if (!bankCode || typeof bankCode !== 'string') {
      console.warn('⚠️ BankService: Invalid bank code provided:', bankCode);
      return bankCode || '';
    }
    
    console.log('🔍 BankService: Resolving bank code to name:', bankCode);
    
    const bankData = await getBankData();
    
    // Look for exact match first
    if (bankData[bankCode]) {
      const bankName = bankData[bankCode].bank_name;
      console.log('✅ BankService: Exact match found:', { bankCode, bankName });
      return bankName;
    }
    
    // Try case-insensitive search
    const upperBankCode = bankCode.toUpperCase();
    for (const [key, value] of Object.entries(bankData)) {
      if (key.toUpperCase() === upperBankCode) {
        const bankName = value.bank_name;
        console.log('✅ BankService: Case-insensitive match found:', { bankCode, key, bankName });
        return bankName;
      }
    }
    
    // Try searching by bank_code property
    for (const [key, value] of Object.entries(bankData)) {
      if (value.bank_code && value.bank_code.toUpperCase() === upperBankCode) {
        const bankName = value.bank_name;
        console.log('✅ BankService: Bank code property match found:', { bankCode, bankName });
        return bankName;
      }
    }
    
    console.warn('⚠️ BankService: No match found for bank code, returning original:', bankCode);
    return bankCode;
    
  } catch (error) {
    console.error('❌ BankService: Error resolving bank name:', {
      bankCode,
      error: error.message
    });
    return bankCode || '';
  }
};

/**
 * Get all available bank codes
 * @returns {Promise<Array>} Array of bank code strings
 */
const getAllBankCodes = async () => {
  try {
    const bankData = await getBankData();
    const bankCodes = Object.keys(bankData);
    console.log('📋 BankService: Available bank codes:', bankCodes.length);
    return bankCodes;
  } catch (error) {
    console.error('❌ BankService: Error getting all bank codes:', error);
    return [];
  }
};

/**
 * Get bank branches for a specific bank code
 * @param {string} bankCode - Bank code to get branches for
 * @returns {Promise<Array>} Array of branch objects with id and branch_name
 */
const getBankBranches = async (bankCode) => {
  try {
    if (!bankCode) {
      return [];
    }
    
    console.log('🏢 BankService: Getting branches for bank code:', bankCode);
    
    const bankData = await getBankData();
    
    // Look for exact match first
    if (bankData[bankCode] && bankData[bankCode].branches) {
      console.log('✅ BankService: Found branches for bank code:', bankCode, 'count:', bankData[bankCode].branches.length);
      return bankData[bankCode].branches;
    }
    
    // Try case-insensitive search
    const upperBankCode = bankCode.toUpperCase();
    for (const [key, value] of Object.entries(bankData)) {
      if (key.toUpperCase() === upperBankCode && value.branches) {
        console.log('✅ BankService: Found branches for bank code (case-insensitive):', key, 'count:', value.branches.length);
        return value.branches;
      }
    }
    
    console.warn('⚠️ BankService: No branches found for bank code:', bankCode);
    return [];
    
  } catch (error) {
    console.error('❌ BankService: Error getting bank branches:', {
      bankCode,
      error: error.message
    });
    return [];
  }
};

/**
 * Get complete bank information (name + branches)
 * @param {string} bankCode - Bank code to get information for
 * @returns {Promise<Object>} Object with bank_name and branches
 */
const getBankInfo = async (bankCode) => {
  try {
    const [bankName, branches] = await Promise.all([
      resolveBankName(bankCode),
      getBankBranches(bankCode)
    ]);
    
    return {
      bank_code: bankCode,
      bank_name: bankName,
      branches: branches
    };
  } catch (error) {
    console.error('❌ BankService: Error getting bank info:', error);
    return {
      bank_code: bankCode,
      bank_name: bankCode,
      branches: []
    };
  }
};

/**
 * Get all bank and branch combinations as dropdown options
 * @returns {Promise<Array>} Array of dropdown options with format: [{value: branchId, label: "BANK_NAME, BRANCH_NAME", ...}]
 */
const getBankBranchOptions = async () => {
  try {
    console.log('🏦 BankService: Generating bank branch dropdown options...');
    
    const bankData = await getBankData();
    const options = [];
    
    for (const [bankCode, bankInfo] of Object.entries(bankData)) {
      const bankName = bankInfo.bank_name || bankCode;
      const branches = bankInfo.branches || [];
      
      // Add each branch as a separate option
      branches.forEach(branch => {
        const branchName = branch.branch_name || branch.name || `Branch ${branch.id}`;
        const branchId = branch.id || branch.branch_id || branch.code;
        
        options.push({
          value: String(branchId), // Branch ID for backend processing
          label: `${bankName}, ${branchName}`, // Display format: "GCB BANK LTD, HIGH STREET"
          bankCode: bankCode,
          bankName: bankName,
          branchName: branchName,
          branchId: branchId,
          // Search terms for filtering (lowercase for case-insensitive search)
          searchTerms: [
            bankName.toLowerCase(),
            branchName.toLowerCase(),
            bankCode.toLowerCase(),
            ...bankName.toLowerCase().split(' '),
            ...branchName.toLowerCase().split(' ')
          ].filter(Boolean)
        });
      });
    }
    
    // Sort options alphabetically by label
    options.sort((a, b) => a.label.localeCompare(b.label));
    
    console.log('✅ BankService: Generated bank branch options:', {
      totalOptions: options.length,
      sampleOptions: options.slice(0, 3).map(opt => ({ value: opt.value, label: opt.label }))
    });
    
    return options;
    
  } catch (error) {
    console.error('❌ BankService: Error generating bank branch options:', error);
    return [];
  }
};

/**
 * Find bank branch option by bank code and branch ID
 * @param {string} bankCode - Bank code from SLAMS
 * @param {string|number} branchId - Branch ID from SLAMS
 * @returns {Promise<Object|null>} Matching option object or null
 */
const findBankBranchOption = async (bankCode, branchId) => {
  try {
    console.log('🔍 BankService: Finding bank branch option:', { bankCode, branchId });
    
    const options = await getBankBranchOptions();
    
    // Try exact match first
    let match = options.find(opt => 
      opt.bankCode === bankCode && String(opt.branchId) === String(branchId)
    );
    
    if (match) {
      console.log('✅ BankService: Exact match found:', match);
      return match;
    }
    
    // Try bank code only match (return first branch for that bank)
    match = options.find(opt => opt.bankCode === bankCode);
    
    if (match) {
      console.log('✅ BankService: Bank code match found (first branch):', match);
      return match;
    }
    
    // Try case-insensitive bank code match
    const upperBankCode = bankCode?.toUpperCase();
    match = options.find(opt => opt.bankCode.toUpperCase() === upperBankCode);
    
    if (match) {
      console.log('✅ BankService: Case-insensitive bank code match found:', match);
      return match;
    }
    
    console.warn('⚠️ BankService: No matching bank branch option found');
    return null;
    
  } catch (error) {
    console.error('❌ BankService: Error finding bank branch option:', error);
    return null;
  }
};

/**
 * Filter bank branch options based on search input
 * @param {Array} options - All bank branch options
 * @param {string} searchText - User input text
 * @returns {Array} Filtered options
 */
const filterBankBranchOptions = (options, searchText) => {
  if (!searchText || typeof searchText !== 'string') {
    return options;
  }
  
  const searchTerms = searchText.toLowerCase().split(' ').filter(Boolean);
  
  return options.filter(option => {
    // Check if all search terms match any of the option's search terms
    return searchTerms.every(searchTerm =>
      option.searchTerms.some(term => term.includes(searchTerm))
    );
  });
};

/**
 * Clear bank data cache (useful for testing or forced refresh)
 */
const clearCache = () => {
  console.log('🗑️ BankService: Clearing bank data cache');
  bankDataCache = null;
  cacheTimestamp = null;
};

const bankService = {
  fetchBankCodesAndBranches,
  getBankData,
  resolveBankName,
  getAllBankCodes,
  getBankBranches,
  getBankInfo,
  getBankBranchOptions,
  findBankBranchOption,
  filterBankBranchOptions,
  clearCache
};

export default bankService;